import { useConfig } from "@/context/ConfigContext";

/**
 * Cogentis wordmark. Uses uploaded logo image when configured (logo_url),
 * otherwise renders a refined typographic wordmark with a geometric mark.
 * variant: "full" | "mark"  |  size in px controls height.
 */
export function Logo({ variant = "full", height = 30, className = "", showTagline = false }) {
  const { config } = useConfig();
  const logoUrl = config?.logo_url;
  const company = config?.company_name || "Cogentis Technologies";

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={company}
        style={{ height }}
        className={`w-auto object-contain ${className}`}
        data-testid="brand-logo-img"
      />
    );
  }

  const markSize = height;
  const mark = (
    <span
      className="relative inline-flex items-center justify-center shrink-0"
      style={{ height: markSize, width: markSize }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 40 40" width={markSize} height={markSize} fill="none">
        <rect
          x="1.5"
          y="1.5"
          width="37"
          height="37"
          rx="9"
          stroke="var(--accent)"
          strokeOpacity="0.35"
          strokeWidth="1.5"
        />
        <circle cx="20" cy="20" r="3.2" fill="var(--accent)" />
        <circle cx="10" cy="11" r="2" fill="#ffffff" fillOpacity="0.9" />
        <circle cx="30" cy="11" r="2" fill="#ffffff" fillOpacity="0.5" />
        <circle cx="10" cy="29" r="2" fill="#ffffff" fillOpacity="0.5" />
        <circle cx="30" cy="29" r="2" fill="#ffffff" fillOpacity="0.9" />
        <path
          d="M10 11 L20 20 L30 11 M10 29 L20 20 L30 29"
          stroke="var(--accent)"
          strokeWidth="1.3"
          strokeOpacity="0.8"
        />
      </svg>
    </span>
  );

  if (variant === "mark") return mark;

  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`} data-testid="brand-logo">
      {mark}
      <span className="flex flex-col leading-none">
        <span
          className="font-display font-semibold tracking-tight text-white"
          style={{ fontSize: height * 0.62 }}
        >
          Cogentis
          <span className="text-white/40 font-light"> Technologies</span>
        </span>
        {showTagline && (
          <span
            className="font-mono text-[var(--accent)]/80 mt-1"
            style={{ fontSize: height * 0.3, letterSpacing: "0.2em" }}
          >
            ENGINEERING CERTAINTY
          </span>
        )}
      </span>
    </span>
  );
}
